import { Injectable } from '@angular/core';
import { AuthService } from '../../shared/services/service';
import { apiUrl } from '../../shared/services/api-url';
import { applyLanguageFromQuery, resolveLanguage } from '../../shared/language/_languages';
import { DEMO_QUESTION_LIMIT, DEMO_SESSION_STORAGE_KEY } from './demo.constants';
import {
  DemoAnswerState,
  DemoApiAnswerResponse,
  DemoApiQuestion,
  DemoCheckResult,
  DemoQuestionView,
  DemoSession,
  defaultDemoTotal,
  emptyAnswerState,
  toQuestionId
} from './demo.models';
import {
  FALLBACK_DEMO_QUESTIONS,
  fallbackHints,
  localizeFallbackQuestion
} from './demo-fallback-questions';

/**
 * Guest demo API contract (AllowAny, no Authorization header).
 *
 * Backend PR: Bkoertge90/medexam-backend#32
 * Production prefix `/backend` is already in environment.apiURL.
 *
 * GET  {api}/demo/questions/?lang=de
 *      List of demo questions (DEMO_QUESTION_IDS, max 10).
 *      Same multilingual fields as the paid question list.
 *
 * GET  {api}/demo/questions/{id}/?lang=de
 *      Detail: option texts, next_prev_question, sources, image.
 *      Correctness is not included until an answer is posted.
 *
 * POST {api}/demo/answers/?lang=de
 *      Same body as authenticated POST /answers/:
 *      { question, is_checked, is_user_check_1..5 }
 *      Response: is_correct + nested option_1..5
 *      (option_is_correct, option_hint, user_check).
 *
 * Never call paid /questions/ or /answers/ from this service (401 for guests).
 * Leftover JWTs are ignored: requests use headerWithoutToken.
 */
@Injectable({
  providedIn: 'root'
})
export class DemoService {
  private session: DemoSession | null = null;
  private startInFlight: Promise<DemoSession> | null = null;
  private loadGeneration = 0;

  constructor(private apiService: AuthService) {
    this.session = this.readStoredSession();
  }

  getSession(): DemoSession | null {
    return this.session;
  }

  hasSession(): boolean {
    return !!this.session && this.session.questionIds.length > 0;
  }

  isCompleted(): boolean {
    return !!(this.session && this.session.completed);
  }

  getTotal(): number {
    return this.session ? this.session.total : DEMO_QUESTION_LIMIT;
  }

  getCurrentIndex(): number {
    return this.session ? this.session.currentIndex : 1;
  }

  getSource(): DemoSession['source'] | null {
    return this.session ? this.session.source : null;
  }

  indexOfQuestion(questionId: number): number {
    if (!this.session) {
      return 0;
    }
    const position = this.session.questionIds.indexOf(questionId);
    return position === -1 ? 0 : position + 1;
  }

  isDemoQuestion(questionId: number): boolean {
    return this.indexOfQuestion(questionId) > 0;
  }

  applyQueryLanguage(langParam?: string | null, languageParam?: string | null): string {
    const before = this.currentLanguage();
    applyLanguageFromQuery(langParam, languageParam);
    return this.syncSessionLanguage(before);
  }

  currentLanguage(): string {
    return resolveLanguage(localStorage.getItem('language'));
  }

  async startSession(forceNew: boolean = false): Promise<DemoSession> {
    this.syncSessionLanguage();
    if (forceNew && this.hasSession()) {
      this.clearSession();
    }
    if (!forceNew && this.hasSession()) {
      return this.session as DemoSession;
    }
    if (this.startInFlight) {
      return this.startInFlight;
    }

    const generation = this.loadGeneration;
    this.startInFlight = this.createSession(generation)
      .then(session => {
        this.startInFlight = null;
        if (generation !== this.loadGeneration || session.language !== this.currentLanguage()) {
          return this.startSession();
        }
        return session;
      })
      .catch(err => {
        this.startInFlight = null;
        throw err;
      });

    return this.startInFlight;
  }

  async getQuestion(index: number): Promise<DemoQuestionView> {
    const session = await this.ensureSession();
    const bounded = this.boundIndex(index, session.total);
    session.currentIndex = bounded;
    this.persist();

    const cached = session.questions[bounded - 1];
    if (session.source === 'fallback' && cached && cached.questionText) {
      return cached;
    }

    if (session.source === 'api') {
      const questionId = session.questionIds[bounded - 1];
      const loaded = await this.fetchApiQuestion(questionId);
      session.questions[bounded - 1] = loaded;
      this.persist();
      return loaded;
    }

    return cached;
  }

  getAnswer(questionId: number): DemoAnswerState {
    if (!this.session) {
      return emptyAnswerState();
    }
    const existing = this.session.answers[questionId];
    if (existing) {
      return existing;
    }
    const created = emptyAnswerState();
    this.session.answers[questionId] = created;
    return created;
  }

  saveSelection(questionId: number, userCheck: boolean[]): void {
    const answer = this.getAnswer(questionId);
    answer.userCheck = userCheck.slice();
    if (this.session) {
      this.session.answers[questionId] = answer;
      this.persist();
    }
  }

  /**
   * Drop a stale "Antwort prüfen" result after the visitor changes the
   * selection of an already-checked question. Persists checked=false and
   * clears correctness, hints and correct options so a reload shows the
   * question as unchecked and score() no longer counts the old result.
   */
  resetCheck(questionId: number): void {
    const answer = this.getAnswer(questionId);
    const cleared = emptyAnswerState();
    answer.checked = false;
    answer.isCorrect = null;
    answer.optionHint = cleared.optionHint;
    answer.optionIsCorrect = cleared.optionIsCorrect;
    if (this.session) {
      this.session.answers[questionId] = answer;
      this.persist();
    }
  }

  async checkAnswer(questionId: number, userCheck: boolean[]): Promise<DemoCheckResult> {
    const session = await this.ensureSession();
    this.saveSelection(questionId, userCheck);

    let result: DemoCheckResult;
    if (session.source === 'api') {
      result = await this.checkApiAnswer(questionId, userCheck);
    } else {
      result = this.gradeFallback(session, questionId, userCheck);
    }

    session.answers[questionId] = {
      userCheck: userCheck.slice(),
      checked: true,
      isCorrect: result.isCorrect,
      optionHint: result.optionHint.slice(),
      optionIsCorrect: result.optionIsCorrect.slice()
    };
    this.persist();
    return result;
  }

  markCompleted(): DemoSession {
    const session = this.session;
    if (!session) {
      throw new Error('No demo session');
    }
    session.completed = true;
    this.persist();
    return session;
  }

  score(): { correct: number; answered: number; total: number } {
    const session = this.session;
    const total = session ? session.total : DEMO_QUESTION_LIMIT;
    if (!session) {
      return { correct: 0, answered: 0, total };
    }
    let correct = 0;
    let answered = 0;
    session.questionIds.forEach(id => {
      const answer = session.answers[id];
      if (answer && answer.checked === true) {
        answered += 1;
        if (answer.isCorrect) {
          correct += 1;
        }
      }
    });
    return { correct, answered, total };
  }

  clearSession(): void {
    this.loadGeneration += 1;
    this.session = null;
    this.startInFlight = null;
    localStorage.removeItem(DEMO_SESSION_STORAGE_KEY);
  }

  private langQuery(): string {
    return '?lang=' + this.currentLanguage();
  }

  private syncSessionLanguage(previousLanguage?: string): string {
    const current = this.currentLanguage();
    const sessionMismatch = !!(this.session && this.session.language !== current);
    const preferenceChanged = !!previousLanguage && previousLanguage !== current;
    if (sessionMismatch || preferenceChanged) {
      this.clearSession();
    }
    return current;
  }

  private async ensureSession(): Promise<DemoSession> {
    this.syncSessionLanguage();
    if (this.hasSession()) {
      return this.session as DemoSession;
    }
    return this.startSession();
  }

  private async createSession(generation: number): Promise<DemoSession> {
    const requestedLang = this.currentLanguage();
    try {
      const res = await this.apiService.ServerGetLogin(apiUrl.demoQuestions + this.langQuery()).toPromise();
      const session = this.sessionFromApi(res);
      if (session.questionIds.length > 0) {
        return this.commitSession(session, generation, requestedLang);
      }
    } catch (err) {
      // Guest API not deployed yet — use bundled sample questions.
    }
    return this.commitSession(this.sessionFromFallback(), generation, requestedLang);
  }

  private commitSession(session: DemoSession, generation: number, requestedLang: string): DemoSession {
    if (generation !== this.loadGeneration || this.currentLanguage() !== requestedLang) {
      return session;
    }
    this.session = session;
    this.persist();
    return session;
  }

  private sessionFromApi(res: unknown): DemoSession {
    const sliced = this.asQuestionArray(res).slice(0, DEMO_QUESTION_LIMIT);
    const questions = sliced.map((item, index) => this.mapApiQuestion(item, index));
    const questionIds = questions.map(q => q.id);

    return {
      sessionId: 'api',
      source: 'api',
      language: this.currentLanguage(),
      total: defaultDemoTotal(questionIds.length),
      currentIndex: 1,
      completed: false,
      questionIds,
      questions,
      answers: {}
    };
  }

  private sessionFromFallback(): DemoSession {
    const language = this.currentLanguage();
    const questions = FALLBACK_DEMO_QUESTIONS.map(q => localizeFallbackQuestion(q, language));
    const correctKeys: { [questionId: number]: boolean[] } = {};
    const hintKeys: { [questionId: number]: string[] } = {};
    FALLBACK_DEMO_QUESTIONS.forEach(q => {
      correctKeys[q.id] = q.correct.slice();
      hintKeys[q.id] = fallbackHints(q, language).slice();
    });

    questions.forEach((question, index) => {
      question.previousQuestion = index > 0 ? questions[index - 1].id : null;
      question.nextQuestion = index < questions.length - 1 ? questions[index + 1].id : null;
    });

    return {
      sessionId: 'local-fallback',
      source: 'fallback',
      language,
      total: DEMO_QUESTION_LIMIT,
      currentIndex: 1,
      completed: false,
      questionIds: questions.map(q => q.id),
      questions,
      answers: {},
      correctKeys,
      hintKeys
    };
  }

  private async fetchApiQuestion(questionId: number): Promise<DemoQuestionView> {
    const url = apiUrl.demoQuestions + questionId + '/' + this.langQuery();
    const res = await this.apiService.ServerGetLogin(url).toPromise();
    return this.mapApiQuestion(res as DemoApiQuestion, 0);
  }

  private async checkApiAnswer(questionId: number, userCheck: boolean[]): Promise<DemoCheckResult> {
    const data = {
      question: questionId,
      is_checked: true,
      is_user_check_1: userCheck[0],
      is_user_check_2: userCheck[1],
      is_user_check_3: userCheck[2],
      is_user_check_4: userCheck[3],
      is_user_check_5: userCheck[4]
    };
    const res = await this.apiService.ServerPostLogin(apiUrl.demoAnswers + this.langQuery(), data).toPromise();
    return this.mapApiAnswer(res as DemoApiAnswerResponse);
  }

  private gradeFallback(
    session: DemoSession,
    questionId: number,
    userCheck: boolean[]
  ): DemoCheckResult {
    const correct = (session.correctKeys && session.correctKeys[questionId]) || [false, false, false, false, false];
    const hints = (session.hintKeys && session.hintKeys[questionId]) || ['', '', '', '', ''];
    let isCorrect = true;
    for (let i = 0; i < 5; i++) {
      if (!!userCheck[i] !== !!correct[i]) {
        isCorrect = false;
        break;
      }
    }
    return {
      isCorrect,
      optionHint: hints.slice(),
      optionIsCorrect: correct.slice()
    };
  }

  private asQuestionArray(res: unknown): DemoApiQuestion[] {
    if (Array.isArray(res)) {
      return res as DemoApiQuestion[];
    }
    if (res && typeof res === 'object') {
      const payload = res as { results?: DemoApiQuestion[]; questions?: DemoApiQuestion[] };
      if (Array.isArray(payload.results)) {
        return payload.results;
      }
      if (Array.isArray(payload.questions)) {
        return payload.questions;
      }
    }
    return [];
  }

  private mapApiQuestion(item: DemoApiQuestion, index: number): DemoQuestionView {
    const optionsFromFields = [
      item && item.option_1_text,
      item && item.option_2_text,
      item && item.option_3_text,
      item && item.option_4_text,
      item && item.option_5_text
    ].map(value => value || '');

    const hasFieldOptions = optionsFromFields.some(value => !!value);
    let options = optionsFromFields;
    if (!hasFieldOptions && item && item.option) {
      options = item.option.map(entry => {
        if (typeof entry === 'string') {
          return entry;
        }
        return (entry && entry.option_text) || '';
      });
      while (options.length < 5) {
        options.push('');
      }
      options = options.slice(0, 5);
    }

    const nextPrev = item && item.next_prev_question;
    return {
      id: toQuestionId(item && item.id) || index + 1,
      questionText: (item && item.question_text) || '',
      image: (item && item.image) || '',
      options,
      sourceName: (item && item.question_source_name) || '',
      sourceLink: (item && item.question_source_link) || '',
      nextQuestion: toQuestionId(nextPrev && nextPrev.next_question),
      previousQuestion: toQuestionId(nextPrev && nextPrev.prev_question)
    };
  }

  private mapApiAnswer(res: DemoApiAnswerResponse): DemoCheckResult {
    const optionResults = [
      res && res.option_1,
      res && res.option_2,
      res && res.option_3,
      res && res.option_4,
      res && res.option_5
    ];
    return {
      isCorrect: !!(res && res.is_correct),
      optionHint: optionResults.map(option => (option && option.option_hint) || ''),
      optionIsCorrect: optionResults.map(option => !!(option && option.option_is_correct))
    };
  }

  private boundIndex(index: number, total: number): number {
    if (!index || index < 1) {
      return 1;
    }
    if (index > total) {
      return total;
    }
    return index;
  }

  private persist(): void {
    if (!this.session) {
      return;
    }
    localStorage.setItem(DEMO_SESSION_STORAGE_KEY, JSON.stringify(this.session));
  }

  private readStoredSession(): DemoSession | null {
    const raw = localStorage.getItem(DEMO_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as DemoSession;
      if (!parsed || !parsed.questionIds || parsed.questionIds.length < 1) {
        return null;
      }
      parsed.total = defaultDemoTotal(parsed.total || parsed.questionIds.length);
      parsed.questionIds = parsed.questionIds.slice(0, DEMO_QUESTION_LIMIT);
      parsed.questions = (parsed.questions || []).slice(0, DEMO_QUESTION_LIMIT);
      return parsed;
    } catch (err) {
      return null;
    }
  }
}
